import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { BillingMetrics, ProductionPipeline, DailyBriefing, CommsCenter, TaskManager } from "../components/dashboard/CommandSections";
import { ReturnsBoard } from "../components/dashboard/ReturnsBoard";
import { FirmDigest } from "../components/dashboard/FirmDigest";
import { GapAnalytics } from "../components/dashboard/GapAnalytics";
import { FlightDeck } from "../components/dashboard/FlightDeck";
import { WeeklyDigest } from "../components/dashboard/WeeklyDigest";
import { LeadsPipeline } from "../components/dashboard/LeadsPipeline";
import { PnlConsole } from "../components/dashboard/PnlConsole";
import { AnalyticsPanel, SecurityHeartbeat, ReferralMetrics } from "../components/dashboard/OpsSections";

export default function Dashboard() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: cc } = useQuery({
    queryKey: ["command-center"],
    queryFn: () => api.get("/dashboard/command-center").then((r) => r.data),
    refetchInterval: 30000,
  });
  const toggle = useMutation({
    mutationFn: (id) => api.patch(`/tasks/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["command-center"] }),
  });

  useEffect(() => {
    api.post("/gaps/remind-check").then(({ data }) => {
      if (data.reminders_sent > 0) toast.info(`Nudged ${data.clients.length} client${data.clients.length > 1 ? "s" : ""} about ${data.reminders_sent} week-old open gap${data.reminders_sent > 1 ? "s" : ""}`);
    }).catch(() => {});
    api.post("/deadlines/remind-check").then(({ data }) => {
      if (data.reminders_sent > 0) toast.info(`Sent ${data.reminders_sent} filing-deadline reminder${data.reminders_sent > 1 ? "s" : ""} to clients`);
    }).catch(() => {});
  }, []);

  return (
    <div className="p-8 space-y-8 fade-up" data-testid="dashboard-page">
      <div>
        <h1 className="font-heading text-2xl font-medium tracking-tight text-zinc-900">Operational Command Center</h1>
        <p className="text-sm text-zinc-500 mt-1">Good day, {user?.name?.split(",")[0]?.split(" ")[0]} · {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</p>
      </div>

      <FlightDeck />
      {["partner", "admin"].includes(user?.role) && <WeeklyDigest />}
      <LeadsPipeline />
      <PnlConsole />
      <FirmDigest />
      <ReturnsBoard board={cc?.returns_board} />
      <GapAnalytics />
      <ProductionPipeline p={cc?.pipeline} />

      <div>
        <p className="text-xs font-medium text-zinc-600 uppercase tracking-wide mb-4">Operations, communication & daily agility</p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <DailyBriefing meetings={cc?.briefing?.meetings} />
          <CommsCenter comms={cc?.comms} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          <TaskManager tasks={cc?.tasks} onToggle={(id) => toggle.mutate(id)} />
          <ReferralMetrics r={cc?.referrals} />
        </div>
      </div>

      <AnalyticsPanel a={cc?.analytics} />
      <BillingMetrics b={cc?.billings} />
      <SecurityHeartbeat s={cc?.security} />
    </div>
  );
}
